import type {
  DealerEvent,
  DealerEventDetail,
  DealerQueueEvent,
  DealerSiteItem,
} from '../../models/entities/dealer-api.model';
import type { EventDetail } from '../../models/entities/event-detail.model';
import type { ClaimEventHandleRequest, EventHandle } from '../../models/entities/event-handle.model';
import type { EventsQueueResponse, QueuedEvent } from '../../models/entities/events-queue.model';
import { formatQueueTimestamp } from '../dto-mappers';

const ISO_WITHOUT_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;

function parseQueueUtcInstant(value: string): Date {
  return new Date(ISO_WITHOUT_OFFSET.test(value) ? `${value}Z` : value);
}

function resolveDisplayTimeZone(timezone?: string | null): string {
  const requested = timezone?.trim();
  if (requested) {
    try {
      new Intl.DateTimeFormat('en-GB', { timeZone: requested }).format(new Date());
      return requested;
    } catch {
      // Invalid IANA — the Events table shows UTC, not the browser zone.
    }
  }
  return 'UTC';
}

function displayTimestamp(value: string | null | undefined, timezone?: string | null): string {
  const trimmed = value?.trim();
  if (!trimmed) {
    return '';
  }

  const date = parseQueueUtcInstant(trimmed);
  if (Number.isNaN(date.getTime())) {
    return trimmed;
  }

  return formatQueueTimestamp(date.toISOString(), resolveDisplayTimeZone(timezone));
}

function formatSiteCurrentTime(timeZone: string | null | undefined): string {
  if (!timeZone?.trim()) {
    return '';
  }

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone.trim(),
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    const hour = parts.find((part) => part.type === 'hour')?.value ?? '00';
    const minute = parts.find((part) => part.type === 'minute')?.value ?? '00';
    const second = parts.find((part) => part.type === 'second')?.value ?? '00';
    const zone = parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
    return `${hour}:${minute}:${second} ${zone}`.trim();
  } catch {
    return '';
  }
}

/** Maps GET /events/{eventId} into the Event View header contract. */
export function mapDealerEventDetailToEventDetail(
  event: DealerEventDetail,
  site?: DealerSiteItem | null,
): EventDetail {
  const location =
    event.siteName?.trim() || site?.siteName?.trim() || `Site ${event.siteId}`;

  return {
    id: event.eventId,
    location,
    eventId: event.eventId,
    address: site?.siteAddress?.trim() ?? '',
    currentTime: formatSiteCurrentTime(site?.timezone),
    siteId: event.siteId,
  };
}

/** Maps Dealer events into the Agent UI queue contract. */
export function mapDealerEventsToQueue(events: DealerEvent[]): EventsQueueResponse {
  const items: QueuedEvent[] = events.map((event) => ({
    id: event.eventId,
    firstAlarmDetected: displayTimestamp(
      event.firstAlarmDateTime?.trim() || event.createdDateTime,
      event.siteTimeZone,
    ),
    location: event.siteName?.trim() || `Site ${event.siteId}`,
    priority: Number(event.priority),
    alarmCount: Number(event.alarmCount),
    siteId: Number(event.siteId),
    type: 'process-event',
  }));

  return {
    totalCount: items.length,
    items,
  };
}

type DealerClaimSession = Pick<
  ClaimEventHandleRequest,
  'tabInstanceId' | 'agentEmail' | 'agentDisplayName'
>;

/** Maps POST /events/claim response into the Agent UI event-handle contract. */
export function mapDealerClaimToEventHandle(
  claimed: DealerQueueEvent,
  session: DealerClaimSession,
  leaseSeconds?: number,
): EventHandle {
  const siteId = Number(claimed.siteId);
  const resolvedLease = Number(leaseSeconds);
  const handleSuffix =
    Number.isFinite(resolvedLease) && resolvedLease > 0
      ? String(resolvedLease)
      : claimed.eventId;

  return {
    handleId: `dealer-${claimed.eventId}-${handleSuffix}`,
    eventId: claimed.eventId,
    tabInstanceId: session.tabInstanceId,
    agentEmail: session.agentEmail,
    agentDisplayName: session.agentDisplayName,
    location: claimed.siteName?.trim() || `Site ${siteId}`,
    firstAlarmDetected:
      claimed.firstAlarmDateTime?.trim() ||
      claimed.createdDateTime?.trim() ||
      new Date().toISOString(),
    priority: Number(claimed.priority ?? 0),
    alarmCount: Number(claimed.alarmCount ?? 0),
    claimedAt: new Date().toISOString(),
    siteId: Number.isInteger(siteId) && siteId > 0 ? siteId : undefined,
    leaseSeconds:
      Number.isFinite(resolvedLease) && resolvedLease > 0 ? resolvedLease : undefined,
  };
}
